/**
 * A manufacturer's video on an upgrade, in the upgrade's own box on the
 * proposal.
 *
 * Jonathon, 2026-10-01: "whenever we allow pivot pipeboot upgrade i want it to
 * show this video", GAF's own introduction to the Master Flow Pivot pipe boot
 * on YouTube. Then, the same day: "now do this one for ice and watershield
 * when we present that upgrade", GAF's StormGuard leak barrier video.
 *
 * Not the walkthrough videos of `ProductVideo` in presentation.ts. Those are
 * our own recordings, uploaded to our storage and shown under the inspection
 * photographs for repairs on the estimate. This is somebody else's video of an
 * upgrade, so it stays on YouTube and sits with the upgrade it explains.
 *
 * Matched on the name, because that is what a document carries. The catalogue
 * calls them "GAF Master Flow™ Pivot™ Pipe Boots" and "High Temperature Ice &
 * Watershield Over Entire Roof". These rules are only ever asked about
 * upgrades, so an ice and water line inside a package never gets a video.
 */

/** A YouTube video, by its id, and what it is called. */
export interface UpgradeVideo {
  /** The part after `youtube.com/watch?v=` or `/embed/`. */
  youtubeId: string;
  /** The video's own title, which is also what the play button says. */
  title: string;
}

interface UpgradeVideoRule {
  matches: (name: string) => boolean;
  video: UpgradeVideo;
}

const RULES: readonly UpgradeVideoRule[] = [
  {
    matches: (name) => /pivot/i.test(name) && /pipe\s*boots?/i.test(name),
    video: {
      youtubeId: "XuFvrJvzgiE",
      title:
        "GAF Master Flow™ Pivot Pipe Boot Flashing - Intro & Basic Installation",
    },
  },
  {
    // The high temperature ice and water shield is GAF StormGuard: the
    // catalogue's own material line reads "High Temp Ice & Water Shield
    // StormGuard".
    matches: (name) => /ice\s*(?:&|and)\s*water\s*-?\s*shield/i.test(name),
    video: {
      youtubeId: "oQ90YNmTAYo",
      title:
        "Help Protect Against Roof Leaks, Water Damage, & More w/ GAF StormGuard® Leak Barrier",
    },
  },
];

/** The video an upgrade carries, by its name, or null. */
export function upgradeVideoFor(name: string): UpgradeVideo | null {
  const rule = RULES.find((candidate) => candidate.matches(name));
  return rule ? { ...rule.video } : null;
}

/**
 * Where the video plays, once the customer asks it to.
 *
 * YouTube's privacy-enhanced address, which sets no cookie until the video
 * plays. Nothing is fetched from YouTube before the customer taps play: the
 * customer's page tells nobody who is reading it (the reason the fonts are
 * self-hosted).
 */
export const youtubeEmbedUrl = (video: UpgradeVideo): string =>
  `https://www.youtube-nocookie.com/embed/${encodeURIComponent(video.youtubeId)}?autoplay=1&rel=0`;
